class ProductQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.minQuantity = parseInt(this.input.getAttribute('min'), 10) || 1;

    // Initially set maxQuantity based on `mm-max` or `mm-stock-max` if available
    this.updateMaxQuantity();

    // Set up a mutation observer to watch for changes to `mm-max` and `mm-stock-max`
    this.observer = new MutationObserver(this.onAttributeChange.bind(this));
    this.observer.observe(this.input, { attributes: true, attributeFilter: ['mm-max', 'mm-stock-max'] });

    // Event listener for manual input changes
    this.input.addEventListener('input', this.onInputChange.bind(this));

    // Find and attach event listeners to the plus and minus buttons
    this.buttonMinus = this.querySelector("button[name='minus']");
    this.buttonPlus = this.querySelector("button[name='plus']");

    if (this.buttonMinus) {
      this.buttonMinus.addEventListener('click', this.decreaseQuantity.bind(this));
    }

    if (this.buttonPlus) {
      this.buttonPlus.addEventListener('click', this.increaseQuantity.bind(this));
    }
  }

  connectedCallback() {
    this.validateQuantity();
  }

  updateMaxQuantity() {
    const mmMax = parseInt(this.input.getAttribute('mm-max'), 10);
    const mmStockMax = parseInt(this.input.getAttribute('mm-stock-max'), 10);
    const fallbackMax = parseInt(this.input.getAttribute('max'), 10);

    // Set maxQuantity to the smaller value of mm-max and mm-stock-max if both exist
    if (!Number.isNaN(mmMax) && !Number.isNaN(mmStockMax)) {
      this.maxQuantity = Math.min(mmMax, mmStockMax);
    } else if (!Number.isNaN(mmMax)) {
      this.maxQuantity = mmMax;
    } else if (!Number.isNaN(mmStockMax)) {
      this.maxQuantity = mmStockMax;
    } else {
      // If neither attribute exists, set maxQuantity to Infinity
      this.maxQuantity = fallbackMax || Infinity;
    }

    // Re-validate quantity limits based on the new maxQuantity
    this.validateQuantity();
  }

  onAttributeChange(mutations) {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'mm-max' || mutation.attributeName === 'mm-stock-max') {
        this.updateMaxQuantity();
      }
    });
  }

  onInputChange() {
    const value = parseInt(this.input.value, 10);
    if (value > this.maxQuantity) {
      this.input.value = this.maxQuantity;
    }
    this.validateQuantity();
  }

  increaseQuantity(event) {
    event.preventDefault();
    if (parseInt(this.input.value, 10) < this.maxQuantity) {
      this.input.stepUp();
      this.validateQuantity();
    }
  }

  decreaseQuantity(event) {
    event.preventDefault();
    if (parseInt(this.input.value, 10) > this.minQuantity) {
      this.input.stepDown();
      this.validateQuantity();
    }
  }

  validateQuantity() {
    const value = parseInt(this.input.value, 10);

    // Disable minus button if at minimum quantity
    if (this.buttonMinus) {
      this.buttonMinus.disabled = value <= this.minQuantity;
      this.buttonMinus.classList.toggle('tw-opacity-50', value <= this.minQuantity);
      this.buttonMinus.classList.toggle('tw-pointer-events-none', value <= this.minQuantity);
    }

    // Disable plus button if at max quantity
    if (this.buttonPlus) {
      this.buttonPlus.disabled = value >= this.maxQuantity;
      this.buttonPlus.classList.toggle('tw-opacity-50', value >= this.maxQuantity);
      this.buttonPlus.classList.toggle('tw-pointer-events-none', value >= this.maxQuantity);
    }
  }

  disconnectedCallback() {
    // Disconnect the mutation observer when the component is removed from the DOM
    this.observer.disconnect();
  }
}

customElements.define('product-quantity-selector', ProductQuantitySelector);
